import React, { useEffect, useState, useCallback } from 'react';
import { guideAPI } from '../api';

const RECOMMENDED_OPTIONS = [
  'photographer',
  'wedding_decorator',
  'interior_designer',
  'brand_designer',
  'event_planner',
];

const emptyDraft = () => ({
  title: '',
  intro: '',
  body: '',
  image_url: '',
  video_url: '',
  recommended_for: [],
  is_published: true,
});

const AdminGuide = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await guideAPI.adminList();
      setSections(res.data.sections || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setDraft({
      title: s.title || '',
      intro: s.intro || '',
      body: s.body || '',
      image_url: s.image_url || '',
      video_url: s.video_url || '',
      recommended_for: s.recommended_for || [],
      is_published: s.is_published !== false,
    });
    setShowNew(false);
  };

  const cancel = () => { setEditingId(null); setDraft(emptyDraft()); setShowNew(false); };

  const save = async () => {
    if (!draft.title.trim()) return;
    try {
      if (editingId) {
        await guideAPI.adminUpdate(editingId, draft);
      } else {
        await guideAPI.adminCreate(draft);
      }
      await load();
      cancel();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await guideAPI.adminDelete(id);
    await load();
  };

  /**
   * Shift the section at `index` up (delta = -1) or down (delta = +1).
   *
   * Performs an optimistic local reorder first so the row visibly moves
   * the instant the admin clicks, then persists the new ordering to the
   * backend. On failure we reload from the server to recover the truth.
   *
   * Calm UX choice: small arrows beat drag-and-drop here. The list is
   * short, the admin is already in a focused editorial mindset, and a
   * one-click shift produces a single discrete movement that's easier
   * to verify visually than a drag gesture.
   */
  const moveSection = async (index, delta) => {
    const next = sections.slice();
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
    try {
      await guideAPI.adminReorder(next.map(s => s.id));
    } catch (e) {
      // Recover from a stale local view if the server rejected the order.
      await load();
      alert(e?.response?.data?.detail || 'Failed to reorder');
    }
  };

  const toggleRec = (val) => {
    setDraft(d => {
      const next = d.recommended_for.includes(val)
        ? d.recommended_for.filter(v => v !== val)
        : [...d.recommended_for, val];
      return { ...d, recommended_for: next };
    });
  };

  return (
    <div data-testid="admin-guide" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 22, margin: 0, color: 'var(--kahla-text)',
          }}>Maestra Guide</h2>
          <p style={{ fontSize: 12, color: 'var(--kahla-text-light)', margin: '6px 0 0' }}>
            Sections shown in Settings → Maestra Guide for all authenticated users.
          </p>
        </div>
        {!showNew && !editingId && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => { setShowNew(true); setDraft(emptyDraft()); }}
            data-testid="admin-guide-add-btn"
            style={{ fontSize: 12 }}
          >
            + Add section
          </button>
        )}
      </div>

      {(showNew || editingId) && (
        <div
          data-testid="admin-guide-editor"
          style={{
            background: '#fff',
            border: '1px solid var(--kahla-border)',
            padding: '22px 24px',
          }}
        >
          <p style={{
            fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase',
            color: 'var(--kahla-text-light)', margin: '0 0 16px',
          }}>
            {editingId ? 'Edit section' : 'New section'}
          </p>
          <div style={{ display: 'grid', gap: 14 }}>
            <input
              type="text"
              placeholder="Title (e.g. How a board flows)"
              value={draft.title}
              onChange={e => setDraft({ ...draft, title: e.target.value })}
              data-testid="admin-guide-title-input"
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Short intro (optional)"
              value={draft.intro}
              onChange={e => setDraft({ ...draft, intro: e.target.value })}
              style={inputStyle}
            />
            <textarea
              placeholder="Body — supports plain text with line breaks."
              value={draft.body}
              onChange={e => setDraft({ ...draft, body: e.target.value })}
              rows={6}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
            <input
              type="text"
              placeholder="Image URL (optional)"
              value={draft.image_url}
              onChange={e => setDraft({ ...draft, image_url: e.target.value })}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Video URL (YouTube / Vimeo, optional)"
              value={draft.video_url}
              onChange={e => setDraft({ ...draft, video_url: e.target.value })}
              style={inputStyle}
            />
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', margin: '0 0 8px' }}>
                Recommended for
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RECOMMENDED_OPTIONS.map(opt => {
                  const on = draft.recommended_for.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleRec(opt)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 11,
                        letterSpacing: '0.06em',
                        textTransform: 'capitalize',
                        border: '1px solid',
                        borderColor: on ? 'var(--kahla-charcoal)' : 'var(--kahla-border)',
                        background: on ? 'var(--kahla-charcoal)' : 'transparent',
                        color: on ? '#fff' : 'var(--kahla-text)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {opt.replace('_', ' ')}
                    </button>
                  );
                })}
              </div>
            </div>
            <label style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'center', color: 'var(--kahla-text)' }}>
              <input
                type="checkbox"
                checked={draft.is_published}
                onChange={e => setDraft({ ...draft, is_published: e.target.checked })}
              />
              Published
            </label>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
            <button type="button" className="btn-secondary" onClick={cancel} style={{ fontSize: 12 }}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={!draft.title.trim()} data-testid="admin-guide-save-btn" style={{ fontSize: 12 }}>
              {editingId ? 'Save changes' : 'Create section'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--kahla-text-light)', fontSize: 12 }}>Loading…</p>
      ) : sections.length === 0 ? (
        <p
          data-testid="admin-guide-empty"
          style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif',
            fontStyle: 'italic',
            color: 'var(--kahla-text-light)',
            background: '#fff',
            border: '1px solid var(--kahla-border)',
            padding: '40px 24px',
            textAlign: 'center',
            margin: 0,
          }}
        >
          No guide sections yet. Add one to begin.
        </p>
      ) : (
        <ul data-testid="admin-guide-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sections.map((s, idx) => (
            <li
              key={s.id}
              data-testid={`admin-guide-row-${s.id}`}
              style={{
                background: '#fff',
                border: '1px solid var(--kahla-border)',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}
            >
              {/* Reorder controls — small vertical pair, edge-aligned so they
                  feel like a margin device rather than a primary action. */}
              <div
                aria-label="Reorder section"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  marginTop: 2,
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => moveSection(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move section up"
                  title="Move up"
                  data-testid={`admin-guide-move-up-${s.id}`}
                  style={{ ...reorderBtn, opacity: idx === 0 ? 0.25 : 0.7, cursor: idx === 0 ? 'default' : 'pointer' }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveSection(idx, +1)}
                  disabled={idx === sections.length - 1}
                  aria-label="Move section down"
                  title="Move down"
                  data-testid={`admin-guide-move-down-${s.id}`}
                  style={{ ...reorderBtn, opacity: idx === sections.length - 1 ? 0.25 : 0.7, cursor: idx === sections.length - 1 ? 'default' : 'pointer' }}
                >
                  ↓
                </button>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 16, color: 'var(--kahla-text)', margin: '0 0 4px' }}>
                  {s.title}{!s.is_published && <span style={{ marginLeft: 10, fontSize: 9, letterSpacing: '0.18em', color: 'var(--kahla-text-light)', textTransform: 'uppercase' }}>Draft</span>}
                </p>
                {s.intro && <p style={{ fontSize: 12, color: 'var(--kahla-text-light)', margin: 0, fontStyle: 'italic' }}>{s.intro}</p>}
                {(s.recommended_for || []).length > 0 && (
                  <p style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', margin: '6px 0 0' }}>
                    {s.recommended_for.join(' · ')}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => startEdit(s)} style={miniBtn}>Edit</button>
                <button type="button" onClick={() => remove(s.id)} style={{ ...miniBtn, color: '#a85a5a' }}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const inputStyle = {
  border: '1px solid var(--kahla-border)',
  background: '#fbf9f4',
  padding: '10px 12px',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const miniBtn = {
  background: 'none',
  border: 'none',
  padding: '4px 0',
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
};

const reorderBtn = {
  background: 'transparent',
  border: '1px solid var(--kahla-border)',
  width: 22,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  color: 'var(--kahla-text)',
  padding: 0,
  fontFamily: 'inherit',
  transition: 'opacity 0.18s ease',
};

export default AdminGuide;
